/**
 * BETA — produit catalogue handlers.
 */
import { httpError } from "../router.js";
import { paginate, matchSearch, newId, nowIso, todayYmd } from "./_utils.js";

export function register(route) {
  route("GET", "/produits", (ctx) => {
    const { params, store } = ctx;
    let rows = [...store.data.produits];
    if (params.include_blocked !== "true") {
      rows = rows.filter((p) => p.statut_produit !== "BLOQUE_CACHE");
    }
    if (params.type_produit) rows = rows.filter((p) => p.type_produit === params.type_produit);
    if (params.liste) rows = rows.filter((p) => p.liste === params.liste);
    if (params.laboratoire_id) rows = rows.filter((p) => p.laboratoire_id === params.laboratoire_id);
    if (params.statut_produit) rows = rows.filter((p) => p.statut_produit === params.statut_produit);
    if (params.search) {
      rows = rows.filter((p) =>
        matchSearch(p, params.search, ["nom_commercial", "dci", "code_barre", "num_enr_cnas"]),
      );
    }
    return paginate(rows, params);
  });

  route("GET", "/produits/alerts", (ctx) => alertsResult(ctx.store));
  route("GET", "/produits/alerts/counts", (ctx) => ({ counts: alertsResult(ctx.store).counts }));

  // REAL laboratoires referenced by THIS catalogue's products, each with its
  // per-lab product count — mirrors the server's GET /produits/laboratoires
  // (groupBy produit.laboratoire_id; NULL + soft-deleted excluded). This handler
  // was missing, so in demo mode the Produits "Laboratoire" filter fell through
  // to the full demo lab list (stub). Now it lists ONLY labs that actually have
  // ≥1 product in the catalogue. Registered BEFORE "/produits/:id" so the
  // "laboratoires" segment is never parsed as a produit id.
  route("GET", "/produits/laboratoires", (ctx) => {
    const counts = new Map();
    for (const p of ctx.store.data.produits) {
      if (p.deleted_at || !p.laboratoire_id) continue;
      counts.set(p.laboratoire_id, (counts.get(p.laboratoire_id) ?? 0) + 1);
    }
    const byId = new Map(
      (ctx.store.data.laboratoires || []).map((l) => [l.id, l]),
    );
    return [...counts.entries()]
      .map(([id, count]) => ({ id, nom: byId.get(id)?.nom ?? null, count }))
      .filter((l) => l.nom != null)
      .sort((a, b) => a.nom.localeCompare(b.nom, "fr", { sensitivity: "base" }));
  });

  route("GET", "/produits/:id", (ctx) => {
    const p = ctx.store.data.produits.find((x) => x.id === ctx.params.id);
    if (!p) httpError(404, "Produit introuvable", "NOT_FOUND");
    return p;
  });

  route("POST", "/produits", (ctx) => {
    const id = newId("p-new");
    const body = ctx.body || {};
    const created = {
      id,
      statut_produit: "ACTIF",
      stock_actuel: 0,
      seuil_alerte: 10,
      stock_alerte: 10,
      tva_pct: 19,
      is_remboursable: false,
      ...body,
      created_at: nowIso(),
    };
    ctx.store.data.produits.push(created);
    ctx.commit();
    return created;
  });

  route("PATCH", "/produits/:id", (ctx) => {
    const p = ctx.store.data.produits.find((x) => x.id === ctx.params.id);
    if (!p) httpError(404, "Produit introuvable", "NOT_FOUND");
    Object.assign(p, ctx.body || {});
    ctx.commit();
    return p;
  });

  route("DELETE", "/produits/:id", (ctx) => {
    const p = ctx.store.data.produits.find((x) => x.id === ctx.params.id);
    if (!p) httpError(404, "Produit introuvable", "NOT_FOUND");
    p.statut_produit = "BLOQUE_CACHE";
    p.deleted_at = nowIso();
    ctx.commit();
    return { success: true };
  });

  route("POST", "/produits/import-chifa", (ctx) => {
    const num = ctx.body?.num_enr;
    const found = ctx.store.data.produits.find((p) => p.num_enr_cnas === num);
    if (found) return { success: true, produit: found, message: "Import simulé (démo)" };
    const stub = {
      id: newId("p-chifa"),
      nom_commercial: "Produit CNAS " + (num || ""),
      dci: "Inconnu",
      forme_pharma: "Comprimé",
      dosage: "—",
      num_enr_cnas: num,
      ppa: 250,
      tr: 250,
      liste: "HORS_LISTE",
      type_produit: "MEDICAMENT",
      is_remboursable: true,
      laboratoire_id: "lab-saidal",
      stock_actuel: 0,
      seuil_alerte: 10,
      stock_alerte: 10,
      statut_produit: "ACTIF",
      created_at: nowIso(),
    };
    return { success: true, produit: stub, message: "Import simulé (démo)" };
  });

  route("POST", "/produits/:id/recalculer-stock", (ctx) => {
    const p = ctx.store.data.produits.find((x) => x.id === ctx.params.id);
    if (!p) httpError(404, "Produit introuvable", "NOT_FOUND");
    return { success: true, stock_recalcule: p.stock_actuel };
  });

  route("PATCH", "/produits/:id/block", (ctx) => {
    const p = ctx.store.data.produits.find((x) => x.id === ctx.params.id);
    if (!p) httpError(404, "Produit introuvable", "NOT_FOUND");
    Object.assign(p, ctx.body || {});
    ctx.commit();
    return p;
  });
}

export function alertsResult(store) {
  const today = todayYmd();
  const plus90 = new Date();
  plus90.setDate(plus90.getDate() + 90);
  const plus90Ymd = plus90.toISOString().slice(0, 10);

  const expired = (store.data.stockLots || []).filter(
    (l) => l.quantite_actuelle > 0 && l.date_peremption < today,
  );
  const nearExpiry = (store.data.stockLots || []).filter(
    (l) =>
      l.quantite_actuelle > 0 &&
      l.date_peremption >= today &&
      l.date_peremption <= plus90Ymd,
  );

  const enrichLot = (l) => {
    const p = store.data.produits.find((x) => x.id === l.produit_id);
    return { ...l, produit: p ? { id: p.id, nom_commercial: p.nom_commercial, dci: p.dci, ppa: p.ppa } : null };
  };

  const lowStock = (store.data.produits || []).filter(
    (p) =>
      p.statut_produit !== "BLOQUE_CACHE" &&
      (p.stock_actuel || 0) > 0 &&
      (p.stock_actuel || 0) < (p.seuil_alerte || 0),
  );
  const outOfStock = (store.data.produits || []).filter(
    (p) => p.statut_produit !== "BLOQUE_CACHE" && (p.stock_actuel || 0) === 0,
  );

  return {
    counts: {
      lotsPerimes: expired.length,
      lotsProchesPeremption: nearExpiry.length,
      produitsStockBas: lowStock.length,
      produitsEnRupture: outOfStock.length,
    },
    expired: expired.map(enrichLot),
    nearExpiry: nearExpiry.map(enrichLot),
    lowStock,
    outOfStock,
  };
}
